var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { D3Component } from "../D3Component/D3Component";
import * as d3 from "d3";
var lightGrey = {
    color: "#EBEDF1",
};
var multipleGrey = { color: "#69717F" };
import styles from "./BubbleChart.module.scss";
import { getColorDecider } from "../../common/action";
var defaultAvailableLabels = [1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0];
var defaultGridPositions = [1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0];
var renderLabels = function (svg, identification, labelFormatter) {
    return function (labels, x, y) {
        return svg
            .selectAll(identification)
            .data(labels)
            .enter()
            .append("text")
            .attr("text-anchor", "center")
            .attr("x", function (value) {
            return (typeof x === "function" ? x(value) : x) +
                labelFormatter(value).offset;
        })
            .attr("y", y)
            .style("font", "500 16px/20px var(--font-stack-full-regular)")
            .style("fill", "#4f5666")
            .text(function (value) { return labelFormatter(value).label; });
    };
};
var rectDirections = {
    vertical: {
        position: "y",
        placement: "x",
        length: "width",
        width: "height",
    },
    horizontal: {
        position: "x",
        placement: "y",
        length: "height",
        width: "width",
    },
};
var getLocalColorDecider = function (colorDescriptions) {
    var decider = getColorDecider(colorDescriptions);
    return function (score) { return decider(score).color; };
};
var renderLabelRects = function (svg, identification, availableColors, showAxisBarBorders) {
    if (showAxisBarBorders === void 0) { showAxisBarBorders = false; }
    return function (direction, scale, min, max, placement, thickness) {
        var gap = 2;
        var r = 4;
        var thresholds = availableColors
            .filter(function (_a) {
            var high = _a.high, low = _a.low;
            return high > min && low < max;
        })
            .map(function (color) { return (__assign(__assign({}, color), { low: color.low >= min ? color.low : min, high: color.high <= max ? color.high : max })); })
            .map(function (color) { return (__assign(__assign({}, color), { low: color.high > color.low ? color.low : color.high, high: color.high < color.low ? color.low : color.high })); })
            .map(function (_a) {
            var low = _a.low, high = _a.high, color = _a.color, border = _a.border;
            return ({
                value: scale(low),
                length: scale(high) - scale(low),
                color: color,
                border: border,
            });
        })
            .map(function (_a) {
            var value = _a.value, length = _a.length, color = _a.color, border = _a.border;
            return ({
                value: length > 0 ? value : value + length,
                length: Math.abs(length),
                color: color,
                border: border,
            });
        })
            .filter(function (_a) {
            var length = _a.length;
            return length > 0;
        });
        svg
            .selectAll(identification)
            .data(thresholds)
            .enter()
            .append("rect")
            .attr(direction.position, function (_a) {
            var value = _a.value;
            return value + gap;
        })
            .attr(direction.placement, placement)
            .attr("ry", r)
            .attr("rx", r)
            .attr(direction.length, thickness)
            .attr(direction.width, function (_a) {
            var length = _a.length;
            return length - gap;
        })
            .style("fill", function (_a) {
            var color = _a.color;
            return color;
        })
            .attr("stroke", showAxisBarBorders
            ? function (_a) {
                var border = _a.border;
                return border || "none";
            }
            : "none")
            .attr("stroke-width", showAxisBarBorders ? 1 : 0);
    };
};
var getMin = function (data, field, lowerBound) {
    var min = Math.min.apply(Math, data.map(function (value) { return value[field]; })) - lowerBound;
    return min;
};
var textPlacer = function (svg) { return function (text, rotation, x, y) {
    svg
        .append("text")
        .attr("x", 0)
        .attr("y", 0)
        .attr("text-anchor", "middle")
        .attr("transform", "translate(".concat(x, ", ").concat(y, "), rotate(").concat(rotation, ")"))
        .text(text)
        .style("font", "500 16px/20px var(--font-stack-full-regular)")
        .style("fill", "#4f5666");
}; };
var innerAxisRenderer = function (svg, identification, availableLabels) {
    return function (direction, scale, range, edges) {
        var position = direction.position, placement = direction.placement;
        var data = availableLabels.filter(function (value) { return value <= range.max && value > range.min; });
        svg
            .selectAll(identification)
            .data(data)
            .enter()
            .append("line")
            .attr("stroke-dasharray", "2,2")
            .attr("".concat(position, "1"), scale)
            .attr("".concat(position, "2"), scale)
            .attr("".concat(placement, "1"), edges.min)
            .attr("".concat(placement, "2"), edges.max)
            .style("stroke", "#d9dce1");
    };
};
var dropdownName = "leaderDropdown";
var escapeHtml = function (value) {
    return value.replace(/[&<>"']/g, function (character) {
        return ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
        }[character] || character);
    });
};
export var getCircledScore = function (decideColor, decideText, formatScore) {
    if (formatScore === void 0) { formatScore = function (score) { return Number(score).toFixed(1); }; }
    return function (score) {
        return "\n  <div class=".concat(styles.circledScore, ">\n    <div class=").concat(styles.scoreCircle, " style=\"background-color: ").concat(decideColor(score), ";\"></div>\n    <div class=").concat(styles.circledScoreText, ">").concat(escapeHtml(formatScore(score)), "</div>\n    <div class=").concat(styles.circledScoreText, ">").concat(escapeHtml(decideText(score)), "</div>\n  </div>\n");
    };
};
export var getNonIntegerCircledScore = function (decideColor, decideText, rounder, decideBorder) {
    if (rounder === void 0) { rounder = function (score) { return Number(score).toFixed(1); }; }
    return function (score) {
        return "\n  <div class=".concat(styles.circledScore, ">\n    <div class=").concat(styles.scoreCircle, " style=\"background-color: ").concat(decideColor(score), "; border: 1px solid ").concat((decideBorder === null || decideBorder === void 0 ? void 0 : decideBorder(score)) || "transparent", ";\"></div>\n    <div class=").concat(styles.circledScoreText, ">").concat(escapeHtml(rounder(score)), "</div>\n    <div class=").concat(styles.circledScoreText, ">").concat(escapeHtml(decideText(score)), "</div>\n  </div>\n");
    };
};
var getLeaderDiv = function (name) { return "\n<div class=".concat(styles.popLeaderName, ">").concat(escapeHtml(name), "</div>"); };
var getContainsRadius = function (_a) {
    var count = _a.count;
    return (24 * Math.sqrt(count || 0)) / 2;
};
var getBubbleTitle = function (_a) {
    var contains = _a.contains, count = _a.count;
    return (count || 0) > 1 ? "".concat(count, " leaders") : (contains === null || contains === void 0 ? void 0 : contains[0]) || "1 leader";
};
var getLeaderPanel = function (title, leaders, scores) {
    return __spreadArray(__spreadArray([
        "<div class=".concat(styles.bubbleTitle, ">").concat(escapeHtml(title), "</div>")
    ], scores, true), (((leaders === null || leaders === void 0 ? void 0 : leaders.length) || 0) > 1
        ? __spreadArray(__spreadArray([
            "<div class=".concat(styles.horizontalRule, "></div>"),
            "<div class=".concat(styles.popLeaderList, ">")
        ], (leaders || []).map(getLeaderDiv), true), [
            "</div>",
        ], false) : []), true).join("");
};
var connectClickable = function (emphasisization, index, nameBase, count) {
    return function () {
        var _loop_1 = function (innerIndex) {
            var bubbleName = "".concat(nameBase, "Circle").concat(innerIndex);
            var bubble = document.querySelector(".".concat(bubbleName));
            var className = "".concat(nameBase, "Popup").concat(innerIndex);
            var showable = document.querySelector(".".concat(className));
            if (!showable) {
                return "continue";
            }
            if (innerIndex == index && showable.style.display === "block") {
                setTimeout(function () {
                    showable.style.display = "none";
                    for (var index_1 = 0; index_1 < count; index_1++) {
                        var circle = document.querySelector(".".concat(nameBase, "Circle").concat(index_1));
                        if (circle) {
                            emphasisization.emphasis(circle);
                        }
                    }
                    return;
                }, 50);
            }
            else {
                setTimeout((function (innerIndex) { return function () {
                    if (innerIndex == index) {
                        showable.style.display = "block";
                        if (bubble) {
                            emphasisization.emphasis(bubble);
                            var parent = bubble === null || bubble === void 0 ? void 0 : bubble.parentNode;
                            parent === null || parent === void 0 ? void 0 : parent.removeChild(bubble);
                            parent === null || parent === void 0 ? void 0 : parent.appendChild(bubble);
                        }
                    }
                    else {
                        showable.style.display = "none";
                        if (bubble) {
                            emphasisization.deEmphasis(bubble);
                        }
                    }
                }; })(innerIndex), 0);
            }
        };
        for (var innerIndex = 0; innerIndex < count; innerIndex++) {
            _loop_1(innerIndex);
        }
    };
};
export var withPanels = function (nameBase, count) {
    return function (doWith) {
        for (var innerIndex = 0; innerIndex < count; innerIndex++) {
            var className = "".concat(nameBase, "Popup").concat(innerIndex);
            var showable = document.querySelector(".".concat(className));
            if (!showable) {
                continue;
            }
            var circle = "".concat(nameBase, "Circle").concat(innerIndex);
            var clickable = document.querySelector(".".concat(circle));
            doWith(showable, clickable);
        }
    };
};
var GreyColorEmphasiser = {
    emphasis: function (clickable) {
        clickable.style.fill = multipleGrey.color;
    },
    deEmphasis: function (clickable) {
        clickable.style.fill = lightGrey.color;
    },
};
var cleanupPanels = function (emphasisization, nameBase, count) { return function () {
    withPanels(nameBase, count)(function (panel, clickable) {
        panel.style.display = "none";
        if (clickable) {
            emphasisization.emphasis(clickable);
        }
    });
}; };
export var havePopup = function (d3, scalers, describeScore, nameBase, identification, length, emphasisization, leftPopup) {
    if (emphasisization === void 0) { emphasisization = GreyColorEmphasiser; }
    return function (point, index) {
        if (point.count == 0) {
            return;
        }
        var clickable = document.querySelector(".".concat(nameBase, "Circle").concat(index));
        var className = "".concat(nameBase, "Popup").concat(index);
        clickable.addEventListener("click", connectClickable(emphasisization, index, nameBase, length));
        var div = document.createElement("div");
        div.classList.add(dropdownName);
        div.classList.add(className);
        div.classList.add(styles.scorePopup);
        div.style.cssText = [
            "top:".concat(scalers.y(point.y) + 1 * Math.sin(Math.PI / 4), "px;"),
            "left: ".concat(leftPopup
                ? leftPopup(point)
                : scalers.x(point.x) +
                    1 * Math.sin(Math.PI / 4) +
                    getContainsRadius(point), "px;"),
        ].join("");
        var panelDocument = new DOMParser().parseFromString(getLeaderPanel(getBubbleTitle(point), point.contains, describeScore(point)), "text/html");
        div.replaceChildren.apply(div, Array.from(panelDocument.body.childNodes));
        var parent = document.querySelector("".concat(identification, "-parent"));
        var d3 = document.querySelector(identification);
        d3.addEventListener("click", cleanupPanels(emphasisization, nameBase, length));
        parent === null || parent === void 0 ? void 0 : parent.appendChild(div);
    };
};
var renderBubbles = function (svg, identification, _a, data, rounder, keyedLabels, decideColor2, popupPlacer, decidePopupBorder) {
    var scaleX = _a.scaleX, scaleY = _a.scaleY;
    var decideColor = function (score) { return decideColor2(score); };
    svg
        .selectAll(identification)
        .data(data)
        .enter()
        .append("circle")
        .attr("class", function (_, index) { return "bubbleCircle".concat(index); })
        .attr("r", getContainsRadius)
        .attr("cx", function (_a) {
        var x = _a.x;
        return scaleX(x);
    })
        .attr("cy", function (_a) {
        var y = _a.y;
        return scaleY(y);
    })
        .style("fill", multipleGrey.color)
        .style("mix-blend-mode", "multiply");
    for (var _i = 0, _b = document.querySelectorAll(".".concat(dropdownName)) || []; _i < _b.length; _i++) {
        var removable = _b[_i];
        removable.remove();
    }
    data.forEach(havePopup(d3, { x: scaleX, y: scaleY }, function (point) { return [
        getNonIntegerCircledScore(decideColor, rounder, function () { return keyedLabels.y.label; }, decidePopupBorder)(point.y),
        getNonIntegerCircledScore(decideColor, rounder, function () { return keyedLabels.x.label; }, decidePopupBorder)(point.x),
    ]; }, "bubble", identification, data.length, GreyColorEmphasiser, popupPlacer));
};
var renderChart = function (rawData, colorDescriptions, availableLabels, gridPositions, axisLabels, rounder, keyedLabels, topPadding, axisFormatter, showAxisBarBorders, popupBorderFunctor) {
    if (topPadding === void 0) { topPadding = 40; }
    if (axisFormatter === void 0) { axisFormatter = function (value) { return ({ label: value.toFixed(1), offset: 0 }); }; }
    return function (svg, identification, _a) {
        var width = _a.width, height = _a.height;
        var data = rawData.map(function (point) {
            var _a;
            return ({
                x: point.x,
                y: point.y,
                contains: point.contains || [],
                count: point.count || ((_a = point.contains) === null || _a === void 0 ? void 0 : _a.length) || 0,
            });
        });
        var decideColor = getLocalColorDecider(colorDescriptions);
        var decidePopupBorder = function (score) {
            return popupBorderFunctor
                ? popupBorderFunctor(score, colorDescriptions)
                : "transparent";
        };
        var lowerBound = { x: 0.3, y: 0.6 };
        var rightMargin = 32;
        var axisLineWidth = 8;
        var axisWidthY = 68;
        var axisHeightX = 65;
        var minX = getMin(data, "x", lowerBound.x);
        var minY = getMin(data, "y", lowerBound.y);
        var maxX = availableLabels[availableLabels.length - 1];
        var maxY = availableLabels[availableLabels.length - 1];
        var getYLabel = d3.scaleLinear([maxY, minY], [topPadding, height - axisHeightX]);
        var getXLabel = d3.scaleLinear([minX, maxX], [axisWidthY, width - rightMargin]);
        var xAxisLabels = availableLabels.filter(function (value) { return value <= maxX && value >= minX; });
        var yAxisLabels = availableLabels.filter(function (value) { return value <= maxY && value >= minY; });
        var labelRenderer = renderLabels(svg, identification, axisFormatter);
        labelRenderer(xAxisLabels, function (value) {
            return getXLabel(value) -
                (xAxisLabels[xAxisLabels.length - 1] == value ? 30 : 10);
        }, height - axisHeightX + 28);
        labelRenderer(yAxisLabels, axisWidthY - 42, function (value) {
            var position = getYLabel(value);
            return position > 16 ? position + 8 : 16;
        });
        var labelRectRenderer = renderLabelRects(svg, identification, colorDescriptions, showAxisBarBorders);
        labelRectRenderer(rectDirections.horizontal, getXLabel, minX, maxX, height - axisHeightX, axisLineWidth);
        labelRectRenderer(rectDirections.vertical, getYLabel, minY, maxY, axisWidthY - axisLineWidth, axisLineWidth);
        var renderInnerAxis = innerAxisRenderer(svg, identification, gridPositions);
        renderInnerAxis(rectDirections.vertical, getYLabel, {
            min: minY,
            max: maxY,
        }, {
            min: getXLabel(minX),
            max: getXLabel(maxX),
        });
        renderInnerAxis(rectDirections.horizontal, getXLabel, {
            min: minX,
            max: maxX,
        }, {
            min: getYLabel(minY),
            max: getYLabel(maxY),
        });
        var placeText = textPlacer(svg);
        placeText(axisLabels.x, 0, Math.abs(getXLabel(maxX) - getXLabel(minX)) / 2, height - 12);
        placeText(axisLabels.y, -90, 12, Math.abs(getYLabel(maxY) - getYLabel(minY)) / 2);
        renderBubbles(svg, identification, { scaleX: getXLabel, scaleY: getYLabel }, data, rounder, keyedLabels, decideColor, function (point) {
            return 1 * Math.sin(Math.PI / 4) +
                (getXLabel(maxX) - 300 < getXLabel(point.x)
                    ? getXLabel(point.x) - getContainsRadius(point) - 240
                    : getXLabel(point.x) + getContainsRadius(point));
        }, decidePopupBorder);
    };
};
export var BubbleChart = function (props) {
    return (_jsx(D3Component, { identification: props.identification, render: renderChart(props.data, props.colorDescriptions, props.availableLabels || defaultAvailableLabels, props.gridPositions || defaultGridPositions, props.axisLabels || {
            x: props.keyedLabels.x.label,
            y: props.keyedLabels.y.label,
        }, props.bubbleScoreRounder ||
            (function (value) { var _a; return ((_a = Number(value)) === null || _a === void 0 ? void 0 : _a.toFixed(1)) || ""; }), props.keyedLabels, props.topPadding, props.axisFormatter, props.showAxisBarBorders, props.popupBorderFunctor), dependencies: [JSON.stringify(props.data)] }));
};
